import type { Toast } from '../contexts/ToastContext';

// Global toast manager for use outside of React components
class GlobalToastManager {
  private listeners: Set<(toast: Omit<Toast, 'id'>) => void> = new Set();

  // Register a toast handler (called by ToastProvider)
  registerHandler(handler: (toast: Omit<Toast, 'id'>) => void) {
    this.listeners.add(handler);
    return () => {
      this.listeners.delete(handler);
    };
  }

  // Private method to add toast
  private addToast(toast: Omit<Toast, 'id'>) {
    if (this.listeners.size === 0) {
      // Fallback to console if no toast provider is registered
      console.warn('No toast provider registered, falling back to console:', toast);
      return;
    }

    this.listeners.forEach(handler => handler(toast));
  }

  // Convenience methods
  success(title: string, message?: string, options?: Partial<Toast>) {
    this.addToast({ ...options, type: 'success', title, message });
  }

  error(title: string, message?: string, options?: Partial<Toast>) {
    this.addToast({ ...options, type: 'error', title, message });
  }

  warning(title: string, message?: string, options?: Partial<Toast>) {
    this.addToast({ ...options, type: 'warning', title, message });
  }

  info(title: string, message?: string, options?: Partial<Toast>) {
    this.addToast({ ...options, type: 'info', title, message });
  }

  // API-specific error handler
  apiError(operation: string, error: any, options?: Partial<Toast>) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    let title = 'API Error';

    // Customize title based on error type
    if (error?.status) {
      switch (error.status) {
        case 401:
          title = 'Authentication Required';
          break;
        case 403:
          title = 'Access Denied';
          break;
        case 404:
          title = 'Not Found';
          break;
        case 422:
          title = 'Validation Error';
          break;
        case 429:
          title = 'Rate Limited';
          break;
        case 500:
        case 502:
        case 503:
        case 504:
          title = 'Server Error';
          break;
        default:
          title = `${operation} Failed`;
      }
    } else {
      title = `${operation} Failed`;
    }

    this.addToast({
      ...options,
      type: 'error',
      title,
      message: errorMessage,
    });
  }

  // Network-specific error handler
  networkError(operation: string, options?: Partial<Toast>) {
    this.addToast({
      ...options,
      type: 'error',
      title: 'Network Error',
      message: `Failed to ${operation}. Please check your internet connection and try again.`,
    });
  }
}

// Export singleton instance
export const globalToast = new GlobalToastManager();
